import type {
  AllStoresSortableColumn,
  ParamsGetMerchflows_merchflowId_StoresAll,
} from "@CommonApi/merchflows/_merchflowId_/stores/all";
import {
  OneStoreViewRow,
  StorePogPdfExport,
  StorePogSummary,
} from "@CommonTypes/merchflow/OneStoreView";
import type { ResponseGetMerchflows_merchflowId_BasePog_basePogId_ } from "@CommonApi/merchflows/_merchflowId_/base-pog/_basePogId_";
import type { ResponseGetMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import { routeToUrl } from "@CommonUtils/routes";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Metric, Tooltip } from "src/components";
import { Checkbox, Color, Flex, Icon, Link, Text } from "src/elements";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";

import { OpenBasePlanogramReviewModal } from "src/modals/BasePlanogramReview/store/hooks";
import { OpenStorePlanogramReviewModal } from "src/modals/StorePlanogramReview/store/hooks";
import { StorePogLogsButton } from "src/pages/Merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_/components/StorePogLogsButton";
import { PogSummaryButton } from "src/components/PogSummary";
import { useTranslation } from "src/i18n";
import { pages } from "src/utils";
import { getCurrentUrl } from "src/utils/history";
import styled from "styled-components";
import type {
  PlanogramPreviewRequestCoordinator,
  PlanogramPreviewTarget,
} from "./planogramPreviewRequests";
import { isStorePogInProgress } from "./utils";
import type { StorePlanogramReviewItem } from "src/modals/StorePlanogramReview/store/hooks";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import { SplitPlanSummary } from "src/components/SplitPlanSummary/SplitPlanSummary";
import { splitPlanNamesOf } from "src/utils/describeSplitPlan";
import { StoreCdtVersionCell } from "../components/StoreCdtVersionCell";
import {
  pdfExportMessageKeys,
  pdfGenerationFailureCategory,
} from "src/api/merchflows/pdfExportErrors";

const ApprovedBubble = styled(Flex)`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${Color.primary};
`;

const listColumnWidth = {
  selection: "50px",
  store: "120px",
  pogAnalysis: "100px",
  pdfStatus: "120px",
  sequence: "90px",
  logs: "60px",
} as const;

const SortHeaderButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${Color.white};
  cursor: pointer;
`;

const StoresAllSortHeader = ({
  label,
  sortKey,
  activeSortBy,
  activeSortDirection,
  onSortChange,
  ariaLabel,
}: {
  label: string;
  sortKey: AllStoresSortableColumn;
  activeSortBy: ParamsGetMerchflows_merchflowId_StoresAll["sortBy"];
  activeSortDirection: ParamsGetMerchflows_merchflowId_StoresAll["sortDirection"];
  onSortChange: (sortKey: AllStoresSortableColumn) => void;
  ariaLabel?: string;
}) => {
  const isSorted = activeSortBy === sortKey && activeSortDirection !== undefined;

  return (
    <SortHeaderButton
      type="button"
      onClick={() => onSortChange(sortKey)}
      aria-label={ariaLabel || `Sort by ${label}`}
      aria-sort={isSorted ? (activeSortDirection === "asc" ? "ascending" : "descending") : "none"}
    >
      <Text variant="small1" color={Color.white} whiteSpace="wrap">
        {label}
      </Text>

      <Icon
        name={
          isSorted
            ? activeSortDirection === "asc"
              ? "triangleSortUp"
              : "triangleSortDown"
            : "triangleSort"
        }
        color={isSorted ? Color.white : Color.spaceGray}
        size={10}
      />
    </SortHeaderButton>
  );
};

const getStorePogMetric = (
  storePog: OneStoreViewRow["store_pog"] | undefined,
  key: Exclude<keyof StorePogSummary, "size">,
): number | null | undefined => {
  if (!storePog) return undefined;
  return storePog.store_pog_summary?.[key] ?? storePog.store_pog_data?.metrics?.[key];
};

const PdfExportStatus = ({ pdfExport }: { pdfExport?: StorePogPdfExport }) => {
  const { t } = useTranslation("common");
  let icon: ReactNode;
  let label: string;

  if (!pdfExport || pdfExport.status === "NOT_STARTED") {
    icon = <Icon name="minus" size={14} color={Color.spaceGray} />;
    label = t("pdfExportStatus.notGenerated");
  } else if (pdfExport.status === "IN_PROGRESS") {
    icon = <Icon name="spinner" size={14} />;
    label = t("pdfExportStatus.generating");
  } else if (pdfExport.status === "DONE") {
    icon = <Icon name="checkmarkCircle" size={14} color={Color.primary} testId="pdf-status-done" />;
    label = t("pdfExportStatus.generated");
  } else {
    icon = <Icon name="warningCircle" size={14} color={Color.red} />;
    label = t("pdfExportStatus.failed");
  }

  return (
    <Flex align="center" aria-label={label}>
      {icon}
      <Tooltip width="300px" whiteSpace="wrap">
        {pdfExport?.status === "ERROR"
          ? `${label}: ${t(pdfExportMessageKeys[pdfGenerationFailureCategory(pdfExport.error)])}`
          : label}
      </Tooltip>
    </Flex>
  );
};

export const getStorePlanogramReviewItems = (
  data: OneStoreViewRow[],
  selectedStorePogId?: number,
  selectedPog?: PogNonCircular,
  selectedResponse?: ResponseGetMerchflowStorePog_storePogId_ | null,
): StorePlanogramReviewItem[] =>
  data.flatMap((row) => {
    const storePog = row.store_pog;
    if (!storePog) return [];

    const isSelected = storePog.store_pog_id === selectedStorePogId;

    return [
      {
        templateId: isSelected
          ? (selectedResponse?.template_id ?? row.template_id)
          : row.template_id,
        storeCode: isSelected ? (selectedResponse?.store ?? row.store) : row.store,
        basePogId: isSelected
          ? (selectedResponse?.base_pog_id ?? row.base_pog?.base_pog_id ?? row.base_pog_id ?? 0)
          : (row.base_pog?.base_pog_id ?? row.base_pog_id ?? 0),
        projectId: row.project_id,
        storePog: {
          storePogId: storePog.store_pog_id,
          storePogData: isSelected ? selectedPog : storePog.store_pog_data,
          isApproved: isSelected
            ? (selectedResponse?.is_approved ?? row.approved_store_pog_amount === 1)
            : row.approved_store_pog_amount === 1,
        },
      },
    ];
  });

export interface StoresAllSortConfig {
  sortBy: ParamsGetMerchflows_merchflowId_StoresAll["sortBy"];
  sortDirection: ParamsGetMerchflows_merchflowId_StoresAll["sortDirection"];
  onSortChange: (sortKey: AllStoresSortableColumn) => void;
}

export interface StoresAllSelectionConfig {
  selection: number[];
  setSelection: (selection: number[]) => void;
  isAllSelected: boolean;
  setIsAllSelected: (isAllSelected: boolean) => void;
  isBulkAllDisabled: boolean;
}

const createSortableMetricColumn = (
  id: string,
  label: string,
  sortKey: AllStoresSortableColumn,
  sortConfig: StoresAllSortConfig,
  renderer: SubgridColumn<OneStoreViewRow>["renderer"],
  options?: { width?: string; ariaLabel?: string },
): SubgridColumn<OneStoreViewRow> => ({
  id,
  header: label,
  headerRenderer: (
    <StoresAllSortHeader
      label={label}
      sortKey={sortKey}
      activeSortBy={sortConfig.sortBy}
      activeSortDirection={sortConfig.sortDirection}
      onSortChange={sortConfig.onSortChange}
      ariaLabel={options?.ariaLabel}
    />
  ),
  width: options?.width ?? "auto",
  justify: "center",
  renderer,
});

/** The Aesthetics column's words in the active locale. */
export interface AestheticsColumnLabels {
  title: string;
  notScored: string;
}

export const useAestheticsColumnLabels = (): AestheticsColumnLabels => {
  const { t } = useTranslation("common");
  return {
    title: t("planogramMetrics.aesthetics.title"),
    notScored: t("metricFilter.notScored"),
  };
};

export const getStoresAllTableColumns = ({
  merchflowId,
  setStoresAllHistory,
  selection: selectionConfig,
  openBasePlanogramReviewModal,
  openStorePlanogramReviewModal,
  data,
  openErrorDetailsModal,
  onBasePogUnavailable,
  sort: sortConfig,
  getMerchflowBasePogApi,
  getMerchflowStorePogApi,
  previewRequestCoordinator,
  loadingPreviewTarget,
  aisleBreaks,
  aestheticsLabels,
  cdtVersion,
}: {
  merchflowId: number;
  /** Adds the "CDT version" column (the version each store resolves to in the flow's category). */
  cdtVersion?: { categoryId: number; header: string };
  setStoresAllHistory: Dispatch<
    SetStateAction<{
      [merchflowId: number]: string;
    }>
  >;
  selection: StoresAllSelectionConfig;
  openBasePlanogramReviewModal: (data: OpenBasePlanogramReviewModal) => void;
  openStorePlanogramReviewModal: (data: OpenStorePlanogramReviewModal) => void;
  data: OneStoreViewRow[];
  openErrorDetailsModal: (errorDetails: string) => void;
  onBasePogUnavailable: () => void;
  sort: StoresAllSortConfig;
  getMerchflowBasePogApi: (
    basePogId: number,
  ) => Promise<ResponseGetMerchflows_merchflowId_BasePog_basePogId_ | null>;
  getMerchflowStorePogApi: (
    storePogId: number,
    includeRanging?: boolean,
  ) => Promise<ResponseGetMerchflowStorePog_storePogId_ | null>;
  previewRequestCoordinator: PlanogramPreviewRequestCoordinator;
  loadingPreviewTarget: PlanogramPreviewTarget | null;
  aisleBreaks?: {
    header: string;
    /** Pogs a Flow Update made that may lay a brand across the store's aisle break. */
    mayCrossStorePogIds: ReadonlySet<number>;
  };
  aestheticsLabels: AestheticsColumnLabels;
}): SubgridColumn<OneStoreViewRow>[] => {
  const { selection, setSelection, isAllSelected, setIsAllSelected, isBulkAllDisabled } =
    selectionConfig;
  // The row says so for good; the Flow Update's status only until the rows are read again.
  const brandsMayCrossAisleBreak = ({ store_pog }: OneStoreViewRow) =>
    store_pog.brands_may_cross_aisle_break === true ||
    (aisleBreaks?.mayCrossStorePogIds.has(store_pog.store_pog_id) ?? false);

  return [
    {
      id: "selection",
      header: "selection",
      width: listColumnWidth.selection,
      justify: "center",
      headerRenderer: (
        <Checkbox
          width="min-content"
          name="select-all-store-pogs"
          isChecked={isAllSelected}
          setIsChecked={setIsAllSelected}
          isDisabled={isBulkAllDisabled}
        />
      ),
      renderer: ({ row: { store_pog } }) => (
        <Checkbox
          width="min-content"
          isChecked={
            store_pog !== undefined
              ? isAllSelected
                ? selection.includes(store_pog.store_pog_id)
                  ? false
                  : true
                : selection.includes(store_pog.store_pog_id)
              : false
          }
          name={store_pog ? `select-store-pog-${store_pog.store_pog_id}` : "select-store-pog"}
          setIsChecked={() => {
            if (store_pog !== undefined) {
              if (selection.includes(store_pog.store_pog_id)) {
                setSelection(selection.filter((s) => s !== store_pog.store_pog_id));
              } else {
                setSelection([...selection, store_pog.store_pog_id]);
              }
            }
          }}
          isDisabled={store_pog === undefined}
        />
      ),
    },
    {
      id: "store",
      header: "Store Code",
      width: listColumnWidth.store,
      renderer: ({ row }) => (
        <Link
          to={`${routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_, {
            merchflowId,
            storeCode: row.store,
          })}`}
          onClick={() => {
            setStoresAllHistory((history) => ({
              ...history,
              [merchflowId]: getCurrentUrl(),
            }));
          }}
        >
          <Text variant="small2">{row.store}</Text>
        </Link>
      ),
    },
    {
      id: "status",
      header: "Store Status",
      justify: "center",
      renderer: ({ row }) => (
        <>
          {row.approved_store_pog_amount === 1 && <Icon name="checkmarkCircle" size={14} />}

          {row.approved_store_pog_amount > 1 && <Icon name="alert" color={Color.red} size={14} />}
        </>
      ),
    },
    {
      id: "template",
      header: "Template",
      subColumns: [
        {
          id: "templateId",
          header: "ID",
          justify: "center",
          renderer: ({ row }) => {
            const basePogId = row.base_pog_id;
            const isLoading =
              loadingPreviewTarget?.type === "base" && loadingPreviewTarget.id === basePogId;

            return basePogId != null ? (
              <Link
                to={`${routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
                  merchflowId,
                  basePogId,
                })}`}
                disabled={isLoading}
                onClick={async (event) => {
                  event.preventDefault();

                  if (row.base_pog?.base_pog_data) {
                    previewRequestCoordinator.cancelPendingRequest();
                    openBasePlanogramReviewModal({
                      merchflowId,
                      basePogId,
                      basePog: row.base_pog.base_pog_data,
                    });
                    return;
                  }

                  const basePogResponse = await previewRequestCoordinator.runLatestRequest(
                    { type: "base", id: basePogId },
                    () => getMerchflowBasePogApi(basePogId),
                  );
                  if (basePogResponse?.pog) {
                    openBasePlanogramReviewModal({
                      merchflowId,
                      basePogId,
                      basePog: basePogResponse.pog,
                      isFavorite: basePogResponse.favorited,
                    });
                  } else if (basePogResponse?.status === "DONE") {
                    onBasePogUnavailable();
                  }
                }}
              >
                <Flex gap="5px" align="center">
                  <Text variant="small2">{basePogId}</Text>
                  {isLoading && <Icon name="spinner" size={12} />}
                </Flex>
              </Link>
            ) : null;
          },
        },
      ],
    },
    {
      id: "planogram",
      header: "Latest Planogram",
      subColumns: [
        createSortableMetricColumn(
          "planogramId",
          "ID",
          "id",
          sortConfig,
          ({ row }) =>
            row.store_pog && (
              <Link
                to={`${routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
                  merchflowId,
                  storeCode: row.store,
                  storePogId: row.store_pog.store_pog_id,
                })}`}
                disabled={
                  loadingPreviewTarget?.type === "store" &&
                  loadingPreviewTarget.id === row.store_pog.store_pog_id
                }
                onClick={async (event) => {
                  event.preventDefault();

                  let storePogData = row.store_pog.store_pog_data;
                  let storePogResponse: ResponseGetMerchflowStorePog_storePogId_ | null = null;

                  if (storePogData) {
                    previewRequestCoordinator.cancelPendingRequest();
                  } else {
                    storePogResponse = await previewRequestCoordinator.runLatestRequest(
                      { type: "store", id: row.store_pog.store_pog_id },
                      // The popup never shows the ranging counts; leaving them out keeps the
                      // first open from reading every store pog in the flow.
                      () => getMerchflowStorePogApi(row.store_pog.store_pog_id, false),
                    );
                    storePogData = storePogResponse?.pog ?? undefined;
                  }

                  if (!storePogData) {
                    return;
                  }

                  openStorePlanogramReviewModal({
                    merchflowId,
                    storePogId: row.store_pog.store_pog_id,
                    storePlanograms: getStorePlanogramReviewItems(
                      data,
                      row.store_pog.store_pog_id,
                      storePogData,
                      storePogResponse,
                    ),
                  });
                }}
              >
                <Flex gap="5px" align="center">
                  <Text variant="small2">{row.store_pog.store_pog_id}</Text>
                  {loadingPreviewTarget?.type === "store" &&
                    loadingPreviewTarget.id === row.store_pog.store_pog_id && (
                      <Icon name="spinner" size={12} />
                    )}
                </Flex>
              </Link>
            ),
          { ariaLabel: "Sort by Latest Planogram ID" },
        ),
        {
          id: "planogramApproved",
          header: "Approved",
          justify: "center",
          renderer: ({ row }) => (
            <>
              {row.approved_store_pog_amount === 1 && (
                <Flex height="min-content" minHeight="14px" align="center">
                  <ApprovedBubble />
                </Flex>
              )}
            </>
          ),
        },
        {
          id: "planogramStatus",
          header: "Status",
          justify: "center",
          renderer: ({ row }) => {
            if (!row.store_pog) return null;
            if (isStorePogInProgress(row.store_pog.status)) {
              return <Icon name="spinner" size={14} />;
            }
            if (row.store_pog.status === "DONE") {
              return <Icon name="checkmarkCircle" size={14} />;
            }
            if (row.store_pog.status === "ERROR") {
              return (
                <Flex
                  onClick={() =>
                    row.store_pog?.error ? openErrorDetailsModal(row.store_pog.error) : ""
                  }
                >
                  <Icon name="warningCircle" size={14} color={Color.red} />
                  {row.store_pog.error && (
                    <Tooltip width="300px" whiteSpace="wrap">
                      <Text
                        variant="small2"
                        color={Color.white}
                        textAlign="center"
                        lineClamp={6}
                        wordBreak="break-all"
                        whiteSpace="wrap"
                      >
                        {row.store_pog.error}
                      </Text>
                    </Tooltip>
                  )}
                </Flex>
              );
            }
            return null;
          },
        },
      ],
    },
    {
      id: "pdfStatus",
      header: "PDF Status",
      width: listColumnWidth.pdfStatus,
      justify: "center",
      align: "start",
      renderer: ({ row }) => <PdfExportStatus pdfExport={row.store_pog?.pdf_export} />,
    },
    ...(cdtVersion
      ? [
          {
            id: "cdtVersion",
            header: cdtVersion.header,
            width: "160px",
            justify: "center" as const,
            renderer: ({ row }: { row: OneStoreViewRow }) => (
              <StoreCdtVersionCell categoryId={cdtVersion.categoryId} storeCode={row.store} />
            ),
          },
        ]
      : []),
    createSortableMetricColumn(
      "returnOnSpace",
      "Return on Space",
      "returnOnSpace",
      sortConfig,
      ({ row }) =>
        row.store_pog && (
          <Metric percent={getStorePogMetric(row.store_pog, "returnOnSpace")} isInline />
        ),
      { width: "120px" },
    ),
    createSortableMetricColumn(
      "coreRange",
      "Core Range",
      "coreRange",
      sortConfig,
      ({ row }) =>
        row.store_pog && (
          <Metric percent={getStorePogMetric(row.store_pog, "coreRange")} isInline />
        ),
      { width: "120px" },
    ),
    createSortableMetricColumn(
      "shelfAlignment",
      "Shelf Alignment",
      "shelfAlignment",
      sortConfig,
      ({ row }) =>
        row.store_pog && (
          <Metric percent={getStorePogMetric(row.store_pog, "shelfAlignment")} isInline />
        ),
      { width: "120px" },
    ),
    createSortableMetricColumn(
      "dosMos",
      "DOS/MOS",
      "dosMos",
      sortConfig,
      ({ row }) =>
        row.store_pog && <Metric percent={getStorePogMetric(row.store_pog, "dosMos")} isInline />,
      { width: "120px" },
    ),
    createSortableMetricColumn(
      "aesthetics",
      aestheticsLabels.title,
      "aesthetics",
      sortConfig,
      ({ row }) => {
        if (!row.store_pog) return null;
        const aesthetics = getStorePogMetric(row.store_pog, "aesthetics");

        // A plan made before the score was stored carries no score. Say so,
        // rather than leaving an empty bar that reads as a zero.
        return aesthetics == null ? (
          <Text variant="small2" color={Color.textSecondary}>
            {aestheticsLabels.notScored}
          </Text>
        ) : (
          <Metric percent={aesthetics} suffix="" isInline />
        );
      },
      { width: "120px" },
    ),
    createSortableMetricColumn(
      "size",
      "Size",
      "size",
      sortConfig,
      ({ row }) =>
        row.store_pog?.store_pog_summary?.size ??
        row.store_pog?.store_pog_data?.planogram.bays.length,
    ),
    {
      id: "pogAnalysis",
      header: "Pog Analysis",
      width: listColumnWidth.pogAnalysis,
      justify: "center",
      renderer: ({ row }) =>
        row.store_pog && (
          <PogSummaryButton
            storePogId={row.store_pog.store_pog_id}
            isCompact
            isEnabled={row.store_pog.status === "DONE"}
          />
        ),
    },
    ...(aisleBreaks &&
    data.some(
      (store) =>
        store.store_pog?.split_plan !== undefined ||
        (store.store_pog && brandsMayCrossAisleBreak(store)),
    )
      ? [
          {
            id: "aisleBreaks",
            header: aisleBreaks.header,
            width: "320px",
            justify: "left" as const,
            renderer: ({ row }: { row: OneStoreViewRow }) =>
              row.store_pog && (
                <SplitPlanSummary
                  plan={row.store_pog.split_plan}
                  cdtNames={
                    row.store_pog.split_plan && row.store_pog.store_pog_data
                      ? splitPlanNamesOf(row.store_pog.store_pog_data)
                      : undefined
                  }
                  brandsMayCrossAisleBreak={brandsMayCrossAisleBreak(row)}
                />
              ),
          },
        ]
      : []),
    // Only include sequence column if any store has sequential mode data
    ...(data.some(
      (store) =>
        store.store_pog?.process_order !== undefined && store.store_pog?.process_order !== null,
    )
      ? [
          createSortableMetricColumn(
            "sequence_order",
            "Sequence",
            "process_order",
            sortConfig,
            ({ row }: { row: OneStoreViewRow }) => {
              // Only show sequence for stores in sequential mode (UHD building blocks mode)
              // Displays the process_order number (1-based display, 0-based data)
              // Column is sortable by process_order (ascending/descending)
              if (
                row.store_pog?.process_order !== undefined &&
                row.store_pog?.process_order !== null
              ) {
                return <Text variant="small2">{row.store_pog.process_order + 1}</Text>;
              }
              return null;
            },
            { width: listColumnWidth.sequence, ariaLabel: "Sort by Process Order" },
          ),
        ]
      : []),
    {
      id: "logs",
      header: "Logs",
      width: listColumnWidth.logs,
      justify: "center" as const,
      renderer: ({ row }) =>
        row.store_pog && (
          <StorePogLogsButton
            merchflowId={merchflowId}
            storeCode={String(row.store)}
            storePogId={row.store_pog.store_pog_id}
            isCompact
          />
        ),
    },
  ] satisfies SubgridColumn<OneStoreViewRow>[];
};
